import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import { CheckCircle, AlertCircle, Clock, Send, Share2, Facebook, Twitter, MessageCircle, Link } from 'lucide-react';
import { useFillTimer } from '../hooks/useFillTimer';
import { enqueteAPI, storage, validation } from '../utils/api';
import CountdownTimer from './CountdownTimer';
import SubheaderVoltar from './common/SubheaderVoltar';
import FooterPublico from './common/FooterPublico';

const FormularioEnquete = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [trackingToken, setTrackingToken] = useState(null);
  const [enqueteEncerrada, setEnqueteEncerrada] = useState(false);
  
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const { startTimer, stopTimer, getCurrentTime, formatTime, isTimerActive } = useFillTimer();

  // Verifica se a enquete está encerrada
  useEffect(() => {
    const dataEncerramento = new Date('2025-11-23T23:59:59');
    const agora = new Date();
    
    if (agora > dataEncerramento) {
      setEnqueteEncerrada(true);
    }
  }, []);

  // Verifica se já votou ao carregar
  useEffect(() => {
    if (storage.hasVoted()) {
      setIsSubmitted(true);
    }
  }, []);

  // Gera token e inicia timer quando o componente carrega
  useEffect(() => {
    const initializeToken = async () => {
      try {
        // Verifica se já tem token válido
        let token = storage.getTrackingToken();
        
        if (!token) {
          // Gera novo token
          const response = await enqueteAPI.gerarToken();
          token = response.token;
          storage.saveTrackingToken(token);
        }
        
        setTrackingToken(token);
        // Inicia o timer automaticamente
        startTimer();
      } catch (error) {
        console.error('Erro ao inicializar token:', error);
        toast.error('Erro ao inicializar sistema de segurança');
      }
    };

    initializeToken();
  }, [startTimer]);

  // Submete o formulário
  const onSubmit = async (data) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      
      // Para o timer e obtém o tempo total
      const fillTime = stopTimer();
      
      // Prepara os dados do voto
      const votoData = {
        ...data,
        tempo_preenchimento_ms: fillTime,
        token_rastreamento: trackingToken,
        // Remove o campo honeypot se estiver vazio (como esperado)
        website_url: data.website_url || ''
      };

      // Valida os dados
      const validationResult = validation.validateFormData(votoData);
      if (!validationResult.isValid) {
        toast.error('Por favor, preencha todos os campos obrigatórios');
        console.error('Erros de validação:', validationResult.errors);
        return;
      }

      // Sanitiza os dados
      const sanitizedData = validation.sanitizeFormData(votoData);

      // Envia o voto
      const response = await enqueteAPI.registrarVoto(sanitizedData);
      
      if (response.success) {
        // Marca como votado
        storage.markAsVoted();
        setIsSubmitted(true);
        
        toast.success('Voto registrado com sucesso! Obrigado pela participação.');
        
        // Scroll para o topo
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

    } catch (error) {
      console.error('Erro ao enviar voto:', error);
      
      // Usa mensagem personalizada do interceptor se disponível
      const message = error.userMessage || 'Erro ao registrar voto. Tente novamente.';
      toast.error(message);
      
      // Comentado: Não marca mais como votado em caso de erro de duplicata
      // if (error.response?.status === 409) {
      //   storage.markAsVoted();
      //   setIsSubmitted(true);
      // }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Se a enquete está encerrada, mostra mensagem
  if (enqueteEncerrada) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50">
        <Toaster position="top-center" />

        {/* Header */}
        <header className="bg-primary text-white shadow-lg">
          <div className="container-responsive py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <img
                  src="/assets/logo_prefeitura_horizontal.png"
                  alt="Prefeitura de Corumbá"
                  className="h-12 w-auto"
                />
              </div>
              <img
                src="/assets/background_corumba.png"
                alt="Corumbá"
                className="hidden lg:block h-12 w-auto"
              />
            </div>
          </div>
        </header>
        <SubheaderVoltar />

        {/* Mensagem de Enquete Encerrada */}
        <main className="container-responsive py-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="card animate-fade-in-up">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Enquete Encerrada
              </h1>
              
              <p className="text-lg text-gray-600 mb-6">
                O prazo para participação nesta enquete foi encerrado em <strong>23 de Novembro de 2025</strong>.
              </p>
              
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
                <p className="text-yellow-800 font-medium">
                  Agradecemos o interesse em participar desta consulta pública!
                </p>
              </div>
              
              <p className="text-sm text-gray-500">
                Para mais informações sobre os resultados, entre em contato com a Prefeitura de Corumbá.
              </p>
            </div>
          </div>
        </main>
        <FooterPublico />
      </div>
    );
  }

  // Se já votou, mostra mensagem de agradecimento
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50">
        <Toaster position="top-center" />

        {/* Header */}
        <header className="bg-primary text-white shadow-lg">
          <div className="container-responsive py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <img
                  src="/assets/logo_prefeitura_horizontal.png"
                  alt="Prefeitura de Corumbá"
                  className="h-12 w-auto"
                />
              </div>
              <img
                src="/assets/background_corumba.png"
                alt="Corumbá"
                className="hidden lg:block h-12 w-auto"
              />
            </div>
          </div>
        </header>
        <SubheaderVoltar />

        {/* Conteúdo de agradecimento */}
        <main className="container-responsive py-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="card animate-fade-in-up">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Obrigado pela sua participação!
              </h1>
              
              <p className="text-lg text-gray-600 mb-6">
                Seu voto foi registrado com sucesso e será considerado na decisão sobre o novo portal de entrada de Corumbá.
              </p>
              
              <div className="bg-primary-50 p-4 rounded-lg mb-6">
                <p className="text-primary-700 font-medium">
                  Sua opinião é fundamental para o desenvolvimento da nossa cidade!
                </p>
              </div>
              
              <p className="text-sm text-gray-500 mb-8">
                Para mais informações, visite o site oficial da Prefeitura de Corumbá.
              </p>

              {/* Contador Regressivo */}
              <div className="mb-8">
                <CountdownTimer />
              </div>

              {/* Seção de Compartilhamento na página de agradecimento */}
              <div className="border-t border-gray-200 pt-8">
                <div className="flex items-center justify-center mb-4">
                  <Share2 className="w-6 h-6 text-primary-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Compartilhe esta enquete
                  </h3>
                </div>
                
                <p className="text-gray-600 mb-6">
                  Convide mais pessoas a participarem desta importante consulta pública!
                </p>
                
                <div className="flex flex-wrap justify-center gap-3">
                  {/* WhatsApp */}
                  <button
                    onClick={() => {
                      const url = encodeURIComponent(window.location.href);
                      const text = encodeURIComponent('🏛️ Acabei de participar da enquete sobre o novo portal de entrada de Corumbá-MS! Participe você também, sua opinião é importante!');
                      window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
                    }}
                    className="flex items-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="hidden sm:inline">WhatsApp</span>
                  </button>

                  {/* Facebook */}
                  <button
                    onClick={() => {
                      const url = encodeURIComponent(window.location.href);
                      window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
                    }}
                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                  >
                    <Facebook className="w-5 h-5" />
                    <span className="hidden sm:inline">Facebook</span>
                  </button>

                  {/* Twitter/X */}
                  <button
                    onClick={() => {
                      const url = encodeURIComponent(window.location.href);
                      const text = encodeURIComponent('🏛️ Acabei de votar na enquete sobre o portal de Corumbá-MS! Participe você também! #Corumbá #ConsultaPública');
                      window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
                    }}
                    className="flex items-center space-x-2 bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                  >
                    <Twitter className="w-5 h-5" />
                    <span className="hidden sm:inline">Twitter</span>
                  </button>

                  {/* Copiar Link */}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href).then(() => {
                        toast.success('Link copiado para a área de transferência!');
                      }).catch(() => {
                        toast.error('Erro ao copiar link');
                      });
                    }}
                    className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                  >
                    <Link className="w-5 h-5" />
                    <span className="hidden sm:inline">Copiar Link</span>
                  </button>
                </div>

                <div className="mt-6 p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-700">
                    <strong>🎉 Obrigado por participar!</strong> Agora ajude a divulgar para que mais pessoas possam contribuir com suas opiniões.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
        <FooterPublico />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50">
      <Toaster position="top-center" />
      
      {/* Header fixo */}
      <header className="bg-primary text-white shadow-lg">
        <div className="container-responsive py-4">
          <div className="relative flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img
                src="/assets/logo_prefeitura_horizontal.png"
                alt="Prefeitura de Corumbá"
                className="h-10 sm:h-12 w-auto"
              />
            </div>

            {/* Timer de preenchimento centralizado absolutamente */}
            {isTimerActive && (
              <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center space-x-1 text-primary-100">
                <Clock className="w-4 h-4" />
                <span className="text-sm">
                  {formatTime(getCurrentTime())}
                </span>
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-end -z-10">
              <img
                src="/assets/background_corumba.png"
                alt="Prefeitura de Corumbá"
                className="h-auto w-100 object-cover"
                style={{ height: 'calc(100% + 2rem)', opacity: 0.6}}
              />
            </div>
          </div>
        </div>
      </header>
      <SubheaderVoltar />

      {/* Conteúdo principal */}
      <main className="container-responsive py-8">
        {/* Título e introdução */}
        <div className="text-center mb-8 animate-fade-in-up">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-4">
            Participe da nossa enquete rápida e ajude a construir o futuro da entrada de Corumbá!
          </h1>
          <p className="text-sm sm:text-xl text-gray-600 mb-2">
            A enquete é anônima e leva apenas alguns minutos para ser preenchida. 
            <br />
            Contamos com a sua participação!
          </p>
          <p className="text-sm sm:text-sm text-gray-600 mb-2"> #NovoPortalCorumba #Corumba #SuaOpiniaoConta #DesenvolvimentoLocal #ParticipacaoCidada</p>
          {/* Imagem do portal atual */}
          <div className="max-w-3xl mx-auto my-8">
            <img 
              src="/assets/portal_quebrado.jpg" 
              alt="Portal atual de Corumbá" 
              className="w-full h-auto rounded-xl shadow-lg"
            />
            <p className="text-sm text-gray-500 mt-2">
              Estado atual do portal de entrada da cidade
            </p>
          </div>
          
          <p className="text-base text-gray-700 max-w-3xl mx-auto text-justify">
            Após o incidente recente com a estrutura atual, a Prefeitura e parceiros, anunciaram a restauração ou a modernização do nosso cartão de visitas. O objetivo é criar um acesso mais seguro, com melhorias no tráfego e uma estrutura que valorize ainda mais a beleza e a cultura de Corumbá.</p>
          <p className="text-base text-gray-700 max-w-3xl mx-auto text-justify">Para garantir que o possível novo portal reflita os desejos e as necessidades da nossa comunidade, queremos ouvir a sua voz! Sua opinião é fundamental para o desenvolvimento deste projeto tão importante para a cidade.</p>

        </div>

        {/* Formulário */}
        <div className="max-w-2xl mx-auto animate-fade-in-up">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              
              {/* Campo Honeypot (oculto) */}
              <input
                {...register('website_url')}
                type="text"
                className="honeypot"
                tabIndex="-1"
                autoComplete="off"
              />

              {/* Pergunta 1: A favor ou contra */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  1. Você é a favor ou contra a construção de um novo portal de entrada para Corumbá?
                </h3>
                
                <div className="space-y-3">
                  {[
                    { value: 'sou_a_favor', label: 'Sou a favor' },
                    { value: 'sou_contra', label: 'Sou contra' },
                    { value: 'nao_tenho_opiniao', label: 'Não tenho opinião formada' }
                  ].map((option) => (
                    <label key={option.value} className="radio-option">
                      <input
                        {...register('res_1_favor_contra', { required: 'Esta pergunta é obrigatória' })}
                        type="radio"
                        value={option.value}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
                
                {errors.res_1_favor_contra && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.res_1_favor_contra.message}
                  </p>
                )}
              </div>

              {/* Pergunta 2: Integração com Ladário */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  2. Considerando a proximidade geográfica, você acredita que o projeto do novo portal de Corumbá deveria integrar ou fazer referência à entrada do município de Ladário?
                </h3>
                
                <div className="space-y-3">
                  {[
                    { value: 'sim_integrar', label: 'Sim, deve integrar/fazer referência' },
                    { value: 'nao_focar_corumba', label: 'Não, deve focar apenas em Corumbá' },
                    { value: 'nao_sei_nao_se_aplica', label: 'Não sei/Não se aplica' }
                  ].map((option) => (
                    <label key={option.value} className="radio-option">
                      <input
                        {...register('res_2_ladario', { required: 'Esta pergunta é obrigatória' })}
                        type="radio"
                        value={option.value}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
                
                {errors.res_2_ladario && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.res_2_ladario.message}
                  </p>
                )}
              </div>

              {/* Pergunta 3: O que incomoda no portal atual */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  3. Em relação ao portal de entrada atual da cidade, qual aspecto mais te incomoda? (Você pode selecionar mais de uma opção)
                </h3>
                
                <div className="space-y-3">
                  {[
                    { name: 'res_3_incomoda_viaria', label: 'A estrutura viária e o fluxo de tráfego na entrada' },
                    { name: 'res_3_incomoda_arquitetura', label: 'O estilo arquitetônico e a estética atual' },
                    { name: 'res_3_incomoda_iluminacao', label: 'A iluminação no local' },
                    { name: 'res_3_incomoda_seguranca', label: 'A segurança no local' }
                  ].map((option) => (
                    <label key={option.name} className="checkbox-option">
                      <input
                        {...register(option.name)}
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
                
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Outro (especifique):
                  </label>
                  <input
                    {...register('res_3_outra_texto')}
                    type="text"
                    className="input-field"
                    placeholder="Descreva outro aspecto que incomoda..."
                  />
                </div>
              </div>

              {/* Pergunta 4: Fauna e Flora */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  4. Você considera que o novo portal deva contemplar elementos da fauna, flora e cultura local de Corumbá?
                </h3>
                
                <div className="space-y-3">
                  {[
                    { value: 'sim', label: 'Sim' },
                    { value: 'nao', label: 'Não' },
                    { value: 'indiferente', label: 'Indiferente' }
                  ].map((option) => (
                    <label key={option.value} className="radio-option">
                      <input
                        {...register('res_4_fauna_flora', { required: 'Esta pergunta é obrigatória' })}
                        type="radio"
                        value={option.value}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
                
                {errors.res_4_fauna_flora && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.res_4_fauna_flora.message}
                  </p>
                )}
              </div>

              {/* Pergunta 5: Novo local */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  5. Você considera que o novo projeto do portal poderia ser construído em um novo local, diferente do atual?
                </h3>
                
                <div className="space-y-3">
                  {[
                    { value: 'sim', label: 'Sim' },
                    { value: 'nao', label: 'Não' },
                    { value: 'talvez_dependendo', label: 'Talvez, dependendo do local' }
                  ].map((option) => (
                    <label key={option.value} className="radio-option">
                      <input
                        {...register('res_5_novo_local', { required: 'Esta pergunta é obrigatória' })}
                        type="radio"
                        value={option.value}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
                
                {errors.res_5_novo_local && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.res_5_novo_local.message}
                  </p>
                )}
              </div>

              {/* Pergunta 6: Expectativa */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  6. Qual a sua principal expectativa para o novo portal de entrada de Corumbá? (Selecione apenas uma opção)
                </h3>
                
                <div className="space-y-3">
                  {[
                    { value: 'trafego_seguranca', label: 'Melhoria no tráfego e segurança' },
                    { value: 'design_moderno', label: 'Design moderno e esteticamente agradável' },
                    { value: 'identidade_cultural', label: 'Representação da identidade cultural e natural da cidade' },
                    { value: 'funcionalidades_adicionais', label: 'Funcionalidades adicionais (ex: centro de informações turísticas)' },
                    { value: 'outra', label: 'Outra' }
                  ].map((option) => (
                    <label key={option.value} className="radio-option">
                      <input
                        {...register('res_6_expectativa', { required: 'Esta pergunta é obrigatória' })}
                        type="radio"
                        value={option.value}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
                
                {watch('res_6_expectativa') === 'outra' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Especifique sua expectativa:
                    </label>
                    <input
                      {...register('res_6_outra_texto', { 
                        required: watch('res_6_expectativa') === 'outra' ? 'Especifique sua expectativa' : false 
                      })}
                      type="text"
                      className="input-field"
                      placeholder="Descreva sua expectativa..."
                    />
                    {errors.res_6_outra_texto && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.res_6_outra_texto.message}
                      </p>
                    )}
                  </div>
                )}
                
                {errors.res_6_expectativa && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.res_6_expectativa.message}
                  </p>
                )}
              </div>

              {/* Pergunta 7: Faixa etária */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  7. Qual faixa etária você se encaixa?
                </h3>
                
                <div className="space-y-3">
                  {[
                    { value: 'abaixo_18', label: 'Abaixo de 18 anos' },
                    { value: '19_44', label: 'Entre 19 - 44 anos' },
                    { value: '45_64', label: 'Entre 45 anos e 64 anos' },
                    { value: '65_mais', label: '65 anos ou mais' }
                  ].map((option) => (
                    <label key={option.value} className="radio-option">
                      <input
                        {...register('res_7_faixa_etaria', { required: 'Esta pergunta é obrigatória' })}
                        type="radio"
                        value={option.value}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
                
                {errors.res_7_faixa_etaria && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.res_7_faixa_etaria.message}
                  </p>
                )}
              </div>

              {/* Pergunta 8: Relação com a cidade */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  8. Qual a sua relação com Corumbá?
                </h3>
                
                <div className="space-y-3">
                  {[
                    { value: 'morador_corumba', label: 'Morador(a) de Corumbá' },
                    { value: 'morador_ladario', label: 'Morador(a) de Ladário' },
                    { value: 'visitante_turista', label: 'Visitante/Turista' },
                    { value: 'trabalhador_regiao', label: 'Trabalhador(a) na região' },
                    { value: 'outra', label: 'Outra' }
                  ].map((option) => (
                    <label key={option.value} className="radio-option">
                      <input
                        {...register('res_8_relacao', { required: 'Esta pergunta é obrigatória' })}
                        type="radio"
                        value={option.value}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
                
                {watch('res_8_relacao') === 'outra' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Especifique sua relação:
                    </label>
                    <input
                      {...register('res_8_outra_texto', { 
                        required: watch('res_8_relacao') === 'outra' ? 'Especifique sua relação' : false 
                      })}
                      type="text"
                      className="input-field"
                      placeholder="Descreva sua relação com a cidade..."
                    />
                    {errors.res_8_outra_texto && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.res_8_outra_texto.message}
                      </p>
                    )}
                  </div>
                )}
                
                {errors.res_8_relacao && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.res_8_relacao.message}
                  </p>
                )}
              </div>

              {/* Botão de envio */}
              <div className="card text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full sm:w-auto inline-flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="loading-spinner"></div>
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Enviar Voto</span>
                    </>
                  )}
                </button>
                
                <p className="mt-4 text-sm text-gray-500">
                  Ao enviar, você confirma que todas as informações são verdadeiras.
                </p>
              </div>
            </form>
        </div>

        {/* Seção de Compartilhamento */}
        <div className="max-w-2xl mx-auto mt-12">
          <div className="card text-center">
            <div className="flex items-center justify-center mb-4">
              <Share2 className="w-6 h-6 text-primary-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">
                Compartilhe esta enquete
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Ajude a divulgar esta importante consulta pública sobre o futuro de Corumbá!
            </p>
            
            <div className="flex flex-wrap justify-center gap-3">
              {/* WhatsApp */}
              <button
                onClick={() => {
                  const url = encodeURIComponent(window.location.href);
                  const text = encodeURIComponent('🏛️ Participe da enquete sobre o novo portal de entrada de Corumbá-MS! Sua opinião é fundamental para o futuro da nossa cidade.');
                  window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
                }}
                className="flex items-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                <MessageCircle className="w-5 h-5" />
                <span className="hidden sm:inline">WhatsApp</span>
              </button>

              {/* Facebook */}
              <button
                onClick={() => {
                  const url = encodeURIComponent(window.location.href);
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
                }}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                <Facebook className="w-5 h-5" />
                <span className="hidden sm:inline">Facebook</span>
              </button>

              {/* Twitter/X */}
              <button
                onClick={() => {
                  const url = encodeURIComponent(window.location.href);
                  const text = encodeURIComponent('🏛️ Participe da enquete sobre o novo portal de entrada de Corumbá-MS! #Corumbá #ConsultaPública');
                  window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
                }}
                className="flex items-center space-x-2 bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                <Twitter className="w-5 h-5" />
                <span className="hidden sm:inline">Twitter</span>
              </button>

              {/* Telegram */}
              <button
                onClick={() => {
                  const url = encodeURIComponent(window.location.href);
                  const text = encodeURIComponent('🏛️ Participe da enquete sobre o novo portal de entrada de Corumbá-MS! Sua opinião é fundamental.');
                  window.open(`https://t.me/share/url?url=${url}&text=${text}`, '_blank');
                }}
                className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                <Send className="w-5 h-5" />
                <span className="hidden sm:inline">Telegram</span>
              </button>

              {/* Copiar Link */}
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href).then(() => {
                    toast.success('Link copiado para a área de transferência!');
                  }).catch(() => {
                    toast.error('Erro ao copiar link');
                  });
                }}
                className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                <Link className="w-5 h-5" />
                <span className="hidden sm:inline">Copiar Link</span>
              </button>

              {/* LinkedIn */}
              <button
                onClick={() => {
                  const url = encodeURIComponent(window.location.href);
                  const title = encodeURIComponent('Enquete Portal de Entrada - Corumbá MS');
                  const summary = encodeURIComponent('Participe da consulta pública sobre o novo portal de entrada de Corumbá-MS');
                  window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}&title=${title}&summary=${summary}`, '_blank');
                }}
                className="flex items-center space-x-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                <span className="hidden sm:inline">LinkedIn</span>
              </button>

              {/* Email */}
              <button
                onClick={() => {
                  const subject = encodeURIComponent('Enquete Portal de Entrada - Corumbá MS');
                  const body = encodeURIComponent(`Olá!\n\nGostaria de convidá-lo(a) a participar da enquete oficial sobre o novo portal de entrada de Corumbá-MS.\n\nSua opinião é fundamental para esta importante decisão que impactará o futuro da nossa cidade.\n\nAcesse: ${window.location.href}\n\nObrigado!`);
                  window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                }}
                className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="hidden sm:inline">Email</span>
              </button>
            </div>

            <div className="mt-6 p-4 bg-primary-50 rounded-lg">
              <p className="text-sm text-primary-700">
                <strong>💡 Dica:</strong> Compartilhe com amigos, familiares e colegas de trabalho. 
                Quanto mais pessoas participarem, mais representativa será a decisão!
              </p>
            </div>
          </div>
        </div>

      </main>
      <FooterPublico />
    </div>
  );
};

export default FormularioEnquete;
